import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styles from './ProductDetail.module.scss';
import { Button, ImageCarousel, DetailsTable, CartCount, } from '@src/components/ui';
import { getProductByPath } from '../../../api/ProductApi';
import { useCart } from '../../../hooks/useCart';
import Toast from '../../../components/Toast';
import { useAuth } from '@src/contexts/AuthContext';
import { formatCurrency } from '@src/utils/formatters';
const ProductDetail = () => {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toastMessage, setToastMessage] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const { isClinicAuthorized } = useAuth();
    const { addItem, isLoading: cartLoading } = useCart();
    const [quantity, setQuantity] = useState(1);
    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await getProductByPath(productId || '');
                const products = response.body;
                if (Array.isArray(products) && products.length > 0) {
                    const productData = products[0];
                    if (!productData.isAvailable) {
                        setError('This product is currently unavailable.');
                        setProduct(null);
                        return;
                    }
                    setProduct(productData);
                    try {
                        const detailedResponse = await fetch(`/api/v1/products/${productData.id}/`, {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                            },
                        });
                        if (detailedResponse.ok) {
                            const detailedProduct = await detailedResponse.json();
                            setProduct((prevProduct) => ({
                                ...prevProduct,
                                ...detailedProduct,
                            }));
                        }
                    }
                    catch (detailErr) {
                        console.log(detailErr);
                    }
                }
                else {
                    setProduct(null);
                    setError('Product not found.');
                }
            }
            catch (err) {
                setError('Failed to load product.');
                setProduct(null);
            }
            finally {
                setLoading(false);
            }
        };
        if (productId) {
            fetchProduct();
        }
    }, [productId]);
    const handleAddToCart = async () => {
        if (!product || cartLoading)
            return;
        try {
            await addItem({
                id: product.id.toString(),
                name: product.title,
                sku: product.sku,
                price: parseFloat(product.price),
                image: product.imageUrlDisplay || '',
                quantity: quantity,
                notDiscountable: product.notDiscountable,
            });
            setToastMessage(`${product.title} added to cart`);
            setIsToastVisible(true);
        }
        catch (err) {
            console.error('Failed to add item to cart:', err);
            setToastMessage('Failed to add item to cart');
            setIsToastVisible(true);
        }
    };
    const handleQuantityChange = (newQuantity) => {
        setQuantity(newQuantity);
    };
    const getProductImages = () => {
        const productWithImages = product;
        if (productWithImages?.images &&
            Array.isArray(productWithImages.images) &&
            productWithImages.images.length > 0) {
            return productWithImages.images
                .slice()
                .sort((a, b) => {
                const aPrimary = a.isPrimary ?? a.is_primary ? 1 : 0;
                const bPrimary = b.isPrimary ?? b.is_primary ? 1 : 0;
                if (bPrimary !== aPrimary)
                    return bPrimary - aPrimary;
                return (a.sortOrder || a.sort_order || 0) - (b.sortOrder || b.sort_order || 0);
            })
                .map((image, index) => ({
                src: image.imageUrl,
                alt: `${product?.title || 'Product'} - Image ${index + 1}`,
                thumbnailSrc: image.imageUrl,
            }));
        }
        else if (product?.imageUrlDisplay) {
            return [
                {
                    src: product.imageUrlDisplay,
                    alt: product.title,
                    thumbnailSrc: product.imageUrlDisplay,
                },
            ];
        }
        return [];
    };
    const getProductDetailsData = () => {
        if (!product)
            return [];
        const rows = [];
        if (product.manufacturer) {
            rows.push({
                label: 'Manufacturer',
                value: product.manufacturer,
            });
        }
        if (product.form || product.type) {
            rows.push({
                label: 'Form',
                value: product.form || product.type || 'Not specified',
            });
        }
        if (product.packaging) {
            rows.push({
                label: 'Packaging',
                value: product.packaging,
            });
        }
        if (product.category) {
            rows.push({
                label: 'Category',
                value: _jsx("span", { style: { color: '#1976d2' }, children: product.category }),
            });
        }
        return rows;
    };
    const getSupportingDocumentsData = () => {
        return [
            {
                icon: 'fas fa-external-link-alt',
                label: 'Safety Data Sheet',
                link: '#'
            },
            {
                icon: 'fas fa-external-link-alt',
                label: 'MSDS',
                link: '#'
            },
            {
                icon: 'fas fa-external-link-alt',
                label: 'Compendium',
                link: '#'
            }
        ];
    };
    if (loading) {
        return (_jsx("div", { className: styles.loadingContainer, children: _jsx("p", { children: "Loading product..." }) }));
    }
    if (error || !product) {
        return (_jsxs("div", { className: styles.notFound, children: [_jsx("h1", { children: "Product Not Found" }), _jsx("p", { children: error || "The product you're looking for doesn't exist." }), _jsx(Link, { to: "/products", children: "Return to All Products" })] }));
    }
    return (_jsxs("main", { className: styles.productDetail, style: { marginTop: '80px' }, children: [_jsx(Toast, { message: toastMessage, isVisible: isToastVisible, onClose: () => setIsToastVisible(false) }), _jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.productLayout, children: [_jsx("div", { className: styles.imageSection, children: _jsx(ImageCarousel, { images: getProductImages(), excludeMainFromThumbnails: true, showImageCounter: false, onImageChange: () => { } }) }), _jsxs("div", { className: styles.detailsSection, children: [_jsx("h1", { className: styles.productTitle, children: product.title }), _jsx("div", { className: styles.price, children: formatCurrency(product.displayPrice || product.price) }), product.isVMGProduct && (_jsxs("div", { className: styles.badges, children: [_jsx("span", { className: styles.vmgBadge, children: "VMG" }), _jsx("span", { className: styles.memberPricingBadge, children: "VMG Member Pricing" })] })), _jsx("p", { className: styles.description, children: product.description }), isClinicAuthorized && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.quantitySection, children: _jsx(CartCount, { value: quantity, onChange: handleQuantityChange, size: "md" }) }), _jsx("div", { className: styles.addToCartSection, children: _jsx(Button, { variant: "primaryV2", size: "large", label: cartLoading
                                                        ? 'Adding...'
                                                        : product.stockQuantity > 0
                                                            ? 'Add to Cart'
                                                            : 'Out of Stock', onClick: handleAddToCart, disabled: !product.isAvailable || product.stockQuantity === 0 }) })] }))] })] }), _jsxs("div", { style: { display: 'flex', gap: '72px' }, children: [_jsx("div", { style: { width: '500px' }, children: getProductDetailsData().length > 0 && (_jsx(DetailsTable, { tableHeading: "Product Details", rows: getProductDetailsData(), hideTopBorder: true })) }), _jsx("div", { style: { width: '500px' }, children: getSupportingDocumentsData().length > 0 && (_jsx(DetailsTable, { tableHeading: "Supporting Documents", rows: getSupportingDocumentsData(), singleColumn: true, hideTopBorder: true })) })] })] })] }));
};
export default ProductDetail;
